import Phaser from 'phaser';
import { Alien, AlienType } from './Alien';
import { GAME_CONFIG } from '../config/gameConfig';

/**
 * AlienGrid: Manages the 5x11 alien formation.
 * Handles grid movement, direction reversal, drop-down, and speed scaling.
 */
export class AlienGrid {
  private scene: Phaser.Scene;
  private aliens: (Alien | null)[][] = [];
  private direction = 1; // 1 = right, -1 = left
  private speed: number;
  private alive = 0;
  private totalAliens: number;

  constructor(scene: Phaser.Scene, wave: number = 1) {
    this.scene = scene;
    const config = GAME_CONFIG.aliens;
    this.speed = config.baseSpeed * Math.pow(GAME_CONFIG.waves.speedMultiplier, wave - 1);
    this.totalAliens = config.rows * config.cols;
    this.alive = this.totalAliens;
  }

  create(): void {
    const config = GAME_CONFIG.aliens;

    for (let row = 0; row < config.rows; row++) {
      this.aliens[row] = [];
      for (let col = 0; col < config.cols; col++) {
        const x = config.startX + col * config.spacingX;
        const y = config.startY + row * config.spacingY;

        let type: AlienType;
        if (row === 0) type = 'top';
        else if (row <= 2) type = 'middle';
        else type = 'bottom';

        this.aliens[row][col] = new Alien(this.scene, x, y, type);
      }
    }
  }

  update(_delta: number): void {
    // Movement logic will be fully implemented by coding agent
    // Basic structure: move all alive aliens in current direction,
    // check boundaries, reverse direction and drop if needed
  }

  getAliveCount(): number {
    return this.alive;
  }

  getTotalAliens(): number {
    return this.totalAliens;
  }

  removeAlien(row: number, col: number): Alien | null {
    const alien = this.aliens[row]?.[col];
    if (alien) {
      this.aliens[row][col] = null;
      this.alive--;
      // Speed up remaining aliens
      this.speed = GAME_CONFIG.aliens.baseSpeed +
        (this.totalAliens - this.alive) * GAME_CONFIG.aliens.speedIncrement;
    }
    return alien;
  }

  /**
   * Remove an alien by reference (used by collision detection).
   * Decrements alive count and nullifies the grid slot.
   */
  removeAlienByRef(target: Alien): boolean {
    for (let row = 0; row < this.aliens.length; row++) {
      for (let col = 0; col < this.aliens[row].length; col++) {
        if (this.aliens[row][col] === target) {
          this.aliens[row][col] = null;
          this.alive--;
          return true;
        }
      }
    }
    return false;
  }

  getAllAliens(): Alien[] {
    const result: Alien[] = [];
    for (const row of this.aliens) {
      for (const alien of row) {
        if (alien) result.push(alien);
      }
    }
    return result;
  }

  destroy(): void {
    for (const row of this.aliens) {
      for (const alien of row) {
        alien?.destroy();
      }
    }
    this.aliens = [];
  }
}
